'use client';

import React from 'react';
import { Quote } from 'lucide-react';
import { cn } from '@/lib/utils'; // Assuming utils/cn exists

interface QuotesSectionProps {
  quote_1?: string | null;
  quote_2?: string | null;
  quote_3?: string | null;
  brandName?: string;
  localBrandName?: string | null;
  allBrandNames?: string[]; // New prop for all brand names from database
}

const isValidQuote = (quote?: string | null): quote is string => {
  return quote !== null && quote !== undefined && quote.trim() !== '' && quote.trim().toUpperCase() !== 'N/A';
};

// Helper function to highlight all brand names that exist in the database
const highlightBrandName = (
  text: string, 
  brandName?: string, 
  localBrandName?: string | null,
  allBrandNames?: string[]
): React.ReactNode[] => {
  // Combine current clip's brand names with all database brand names
  const currentBrandNames = [brandName, localBrandName]
    .filter((name): name is string => !!name && name.trim() !== '');
  
  const databaseBrandNames = allBrandNames || [];
  
  // Create a set to avoid duplicates and filter out empty names
  const allNamesToHighlight = Array.from(new Set([
    ...currentBrandNames,
    ...databaseBrandNames
  ]))
    .filter(name => name && name.trim() !== '')
    .map(name => name.replace(/[.*+?^${}()|[\]\\]/g, '\\\\$&')); // Escape regex special chars

  if (allNamesToHighlight.length === 0) return [text]; // Return original text if no names

  // Create a combined regex (e.g., (BrandName1|BrandName2|LocalName))
  const regex = new RegExp(`(${allNamesToHighlight.join('|')})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, index) => {
    // Check if the part matches any of the names (case-insensitive)
    const isMatch = allNamesToHighlight.some(name => part.toLowerCase() === name.toLowerCase());
    
    if (isMatch) {
      // Check if this is the current clip's brand name for special styling
      const isCurrentBrand = currentBrandNames.some(name => part.toLowerCase() === name.toLowerCase());
      
      return (
        <span 
          key={index} 
          className={`font-bold ${
            isCurrentBrand 
              ? 'bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent' 
              : 'bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent'
          }`}
        >
          {part}
        </span>
      );
    }
    return part;
  });
};

const QuotesSection: React.FC<QuotesSectionProps> = ({ 
  quote_1, 
  quote_2, 
  quote_3, 
  brandName, 
  localBrandName,
  allBrandNames 
}) => {
  const quotes = [quote_1, quote_2, quote_3].filter(isValidQuote);

  if (quotes.length === 0) {
    return null; // Don't render the section if there are no valid quotes
  }

  return (
    <div>
      <div className="space-y-4">
        {quotes.map((quote, index) => (
          <blockquote 
            key={index} 
            className="relative pl-10 pr-6 py-4 border-l-4 border-purple-600 bg-purple-50 dark:bg-gray-800 dark:border-purple-500 rounded-r-lg shadow-sm"
          >
            <Quote 
              className="absolute top-3 left-3 h-8 w-8 text-purple-200 dark:text-purple-700 opacity-50" 
              aria-hidden="true"
              fill="currentColor"
            />
            <p className="text-gray-700 dark:text-gray-300 italic leading-relaxed text-justify">
              {/* Use the helper function to render the quote with all brand names highlighted */}
              {highlightBrandName(quote, brandName, localBrandName, allBrandNames)}
            </p>
            {/* Optional: Add attribution or source if available */}
            {/* <footer className="mt-2 text-sm text-gray-500 dark:text-gray-400">Source/Attribution</footer> */}
          </blockquote>
        ))}
      </div>
    </div>
  );
};

export default QuotesSection; 